import { EPaymentFrequency } from '@if/core/types'
import { getEnvRuntimeVariable, Getter } from '@if/core/utils'
import { useTranslatedOptionEnum } from '@if/mui'
import { MenuItem } from '@mui/material'
import { createElement } from 'react'
import { useTranslation } from 'react-i18next'
import { lazyLoad } from './lazy'

export const SYSTEM_CONSTANT_CHECK_FOR_STALE_STATE_INTERVAL = 30 * 1000

export const REPLAY_MIGRATION_COUNTDOWN_SECONDS = 3

export enum ETerm {
  Twelve = 12,
  TwentyFour = 24,
  ThirtySix = 36,
  FortyEight = 48,
  Sixty = 60,
  SeventyTwo = 72,
  EightyFour = 84,
}

export enum ELoanStatus {
  Authorized = 1,
  Active = 2,
  Suspended = 3,
  Closed = 4,
  NotRequired = 5,
}

export enum EProduct {
  NsfFee = 1,
  AdminFee = 2,
  FinanceFee = 3,
  CapitalFinanced = 4,
  IFinanceInsurance = 5,
  Interest = 6,
  CreditInsurance = 7,
  PaymentInsurance = 8,
  ReplacementInsurance = 9,
  ClientInsurance = 12,
  MechanicalWarranty = 17,
  ProtectionPlan = 22,
  RoadSide = 28,
}

export enum EDisbursementTarget {
  Merchant = 1,
  Borrower = 2,
}

export enum ECountryIso {
  Canada = 'CA',
}

export enum EFeeBillingMechanism {
  ChargedOnDisbursement = 1,
  ChargedAfterPromo = 2,
  ChargedOnAnniversary = 3,
}

export enum EInterestRoundingMethod {
  Round = 1,
  Trunk = 2,
}

export enum EInterestComputeMethod {
  OutstandingPrincipal = 1,
  // Reserved for legacy migrated loans; not offered when creating a loan.
  OutstandingPrincipalLegacy = 2,
  UnbilledPrincipal = 3,
}

export enum EOperationStatus {
  Planned = 1,
  Cancelled = 2,
  Complete = 3,
  Failed = 4,
}

export enum ECatchUpLatePaymentMethod {
  CatchUpOnSinglePayment = 'catchUpOnSinglePayment',
  CatchUpOnRemainingPayments = 'catchUpOnRemainingPayments',
  CatchUpOnNextPayments = 'catchUpOnNextPayments',
}

export const EXECUTED_OPERATION_STATUSES = [EOperationStatus.Complete, EOperationStatus.Failed]

export enum EBankFailureCode {
  REVAdministrator = 555,
  RejectedFollowingEditing = 900,
  NSFInsufficientFunds = 901,
  AccountNotFound = 902,
  PaymentOppositionRevocation = 903,
  PostdatedExpiredCheque = 904,
  AccountClosed = 905,
  AccountTransferred = 906,
  NoChequePrivilege = 907,
  UnclearedFunds = 908,
  Deceased = 910,
  AccountFrozen = 911,
  IncorrectAccountNumber = 912,
  NameError = 914,
  RefusedByDebtor = 915,
  NonCompliantWithAgreementPersonal = 916,
  AgreementRevokedPersonal = 917,
  NoPriorNotificationPersonal = 918,
  NonCompliantWithAgreementBusiness = 919,
  AgreementRevokedBusiness = 920,
  NoPriorNotificationBusiness = 921,
  CreditReturnInitiatedByClient = 922,
  DefaultingInstitution = 990,
}

// Values map to [Loan].[TMetro2ReportingReason].Id. Labels live locally under
// translation:enum.EMetro2ReportingReason.<value>.
export enum EMetro2ReportingReason {
  IcebergError = 1,
  ClientChance = 2,
}
export const getEMetro2ReportingReasonList: Getter<EMetro2ReportingReason[]> = lazyLoad(() =>
  Object.values(EMetro2ReportingReason).filter((x): x is EMetro2ReportingReason => typeof x === 'number'),
)
export const useTranslatedMetro2ReportingReasonList = () => {
  return useTranslatedOptionEnum('enum.EMetro2ReportingReason', getEMetro2ReportingReasonList())
}

export const getELoanStatusList: Getter<ELoanStatus[]> = lazyLoad(() =>
  Object.values(ELoanStatus).filter((x) => typeof x === 'number'),
)
export const getEProductList: Getter<EProduct[]> = lazyLoad(() =>
  Object.values(EProduct).filter((x) => typeof x === 'number'),
)
// A journal-transaction line whose ProductId is -1 targets a system GL account instead of a product.
export const SYSTEM_ACCOUNT_PRODUCT_ID = -1
// Numeric enum mirroring the base GL account numbers of the backend system accounts
// (LoanApi.Data.Types.Accounting.SystemAccountsEnum, numbers assigned in
// AccountingAccountCreator.CreateSystemAccountsForOwner, and ClosingAuxiliary in
// CreateClosingOwnerForIcebergProductAccounts). A securitized owner offsets its
// accounts by a whole-million prefix (LoanOwner.GetAccountPrefix), so resolve a line's account
// with accountNumber % OWNER_ACCOUNT_PREFIX_SPAN. Labels live locally under
// translation:enum.ESystemAccount.<value>.
export enum ESystemAccount {
  ReceivedAmount = 10001,
  Bank = 10002,
  DirectDepositReimbursement = 10010,
  CustomerToPay = 10020,
  ClosingAuxiliary = 12055,
  ProvidersToPay = 21500,
}
export const OWNER_ACCOUNT_PREFIX_SPAN = 1_000_000
// Numeric enum mirroring the backend LoanApi.Data.Enums.AccountClassificationTypeEnum: the role a journal-transaction
// line's GL account plays for its product. Labels live locally under translation:enum.EAccountType.<value>.
export enum EAccountType {
  Receivable = 1,
  Income = 2,
  Expense = 3,
  Loss = 4,
  Waived = 5,
  Recovery = 6,
}
export const getESystemAccountList: Getter<ESystemAccount[]> = lazyLoad(() =>
  Object.values(ESystemAccount).filter((x) => typeof x === 'number'),
)
export const getEDisbursementTargetList: Getter<EDisbursementTarget[]> = lazyLoad(() =>
  Object.values(EDisbursementTarget).filter((x) => typeof x === 'number'),
)
export const getETermList: Getter<ETerm[]> = lazyLoad(() => Object.values(ETerm).filter((x) => typeof x === 'number'))
export const SUPPORTED_DOCUMENT_TYPES = '.jpg, .jpeg, .png, .pdf, .heic, .heif'
export const SYSTEM_USER_ID = 1
export const CLEAR_CACHE_EXCEPTION_KEY = ['mui-mode']
export const SELECTED_COMPANY_STORAGE_KEY = 'selectedCompany'
export const getEFeeBillingMechanismList: Getter<EFeeBillingMechanism[]> = lazyLoad(() =>
  Object.values(EFeeBillingMechanism).filter((x) => typeof x === 'number'),
)
export const getEInterestRoundingMethodList: Getter<EInterestRoundingMethod[]> = lazyLoad(() =>
  Object.values(EInterestRoundingMethod).filter((x) => typeof x === 'number'),
)
export const getEInterestComputeMethodList: Getter<EInterestComputeMethod[]> = lazyLoad(() =>
  Object.values(EInterestComputeMethod).filter((x) => typeof x === 'number'),
)
export const SUPPORTED_FREQUENCIES = [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly]
export const useTranslatedPaymentFrequency = () => {
  return useTranslatedOptionEnum('EPaymentFrequency', SUPPORTED_FREQUENCIES, 'enum')
}
// Numeric enum mirroring the backend LoanApi.Data.Enums.PaymentMethods (string codes
// retired). Labels live locally under translation:enum.EPaymentMethod.<value>.
export enum EPaymentMethod {
  Cheque = 1,
  BankTransfer = 2,
  DEFT = 3,
  WIRE = 4,
  PreAuthorizedDebit = 5,
  Cash = 6,
  InteracETransfer = 7,
  APTPay = 8,
  Waived = 102,
}
export const getEPaymentMethodList: Getter<EPaymentMethod[]> = lazyLoad(() =>
  Object.values(EPaymentMethod).filter((x): x is EPaymentMethod => typeof x === 'number'),
)
export const VALID_PAYMENT_METHODS = [
  EPaymentMethod.PreAuthorizedDebit,
  EPaymentMethod.Cheque,
  EPaymentMethod.Cash,
  EPaymentMethod.InteracETransfer,
  EPaymentMethod.Waived,
]
export const useTranslatedPaymentMethods = () => {
  return useTranslatedOptionEnum('enum.EPaymentMethod', VALID_PAYMENT_METHODS)
}
// A provider sends a product refund by cheque, Interac or cash; it is never collected by pre-authorized debit.
export const PRODUCT_REFUND_PAYMENT_METHODS = [
  EPaymentMethod.Cheque,
  EPaymentMethod.InteracETransfer,
  EPaymentMethod.Cash,
]
export const useTranslatedProductRefundPaymentMethods = () => {
  return useTranslatedOptionEnum('enum.EPaymentMethod', PRODUCT_REFUND_PAYMENT_METHODS)
}
export const VALID_CATCHUP_LATE_PAYMENT_METHODS = [
  ECatchUpLatePaymentMethod.CatchUpOnSinglePayment,
  ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments,
  ECatchUpLatePaymentMethod.CatchUpOnNextPayments,
]

export const useTranslatedCatchUpLatePaymentMethods = () => {
  const { t } = useTranslation()
  return VALID_CATCHUP_LATE_PAYMENT_METHODS.map((method) =>
    createElement(MenuItem, { key: method, value: method }, t(`enum.ECatchUpLatePaymentMethod.${method}`)),
  )
}

export const getEBankFailureCodeList: Getter<EBankFailureCode[]> = lazyLoad(() =>
  Object.values(EBankFailureCode).filter((x) => typeof x === 'number'),
)

export const useTranslatedBankFailureCode = () => {
  return useTranslatedOptionEnum('enum.bankFailureCode', getEBankFailureCodeList())
}

export const getEOperationStatusList: Getter<EOperationStatus[]> = lazyLoad(() =>
  Object.values(EOperationStatus).filter((x) => typeof x === 'number'),
)

export const falsy = () => false

// To disable it just set the env var to an empty string
export const useDevTools = getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_DEV_TOOLS)
// Runtime read: a plain comparison is constant-folded at build, before the deploy-time token replacement
export const isProd = getEnvRuntimeVariable(import.meta.env.VITE_ENV) === 'prod'

export const DEFAULT_PAYMENT_METHOD = EPaymentMethod.PreAuthorizedDebit

export enum EMetro2AccountStatus {
  Current = 0,
  ThirtyDays = 1,
  SixtyDays = 2,
  NinetyDays = 3,
  OneTwentyDays = 4,
  OneFiftyDays = 5,
  OneEightyDays = 6,
}

export const getEMetro2AccountStatusList: Getter<EMetro2AccountStatus[]> = lazyLoad(() =>
  Object.values(EMetro2AccountStatus).filter((x) => typeof x === 'number'),
)

export enum EFeeType {
  GeneralFees = 'generalFees',
  NsfFees = 'nsfFees',
}

const PRODUCT_TO_FEE_TYPE: Record<number, EFeeType> = {
  [EProduct.AdminFee]: EFeeType.GeneralFees,
  [EProduct.NsfFee]: EFeeType.NsfFees,
}

export const getFeeTypeFromProduct = (productId: EProduct): EFeeType | null => {
  return PRODUCT_TO_FEE_TYPE[productId] ?? null
}

export const VALID_FEE_TYPES = [EFeeType.GeneralFees, EFeeType.NsfFees]

export const useTranslatedFeeTypeList = () => {
  return useTranslatedOptionEnum('enum.EFeeType', VALID_FEE_TYPES)
}

// Numeric enum mirroring the backend LoanApi.Data.Enums.FinancingPlanEnum (ids match the
// [Loan].[TFinancingPlan] lookup). Replaces the @if/core EPlanType for loans, whose value
// set diverged from the backend. Labels live locally under translation:enum.EFinancingPlan.<value>.
export enum EFinancingPlan {
  AutoBronze = 1,
  AutoBronzeND = 2,
  AutoGold = 3,
  AutoGoldND = 4,
  AutoSilver = 5,
  AutoSilverND = 6,
  IfpBronze = 7,
  IfpGold = 8,
  IfpSilver = 9,
  Preplan = 10,
}
export const getEFinancingPlanList: Getter<EFinancingPlan[]> = lazyLoad(() =>
  Object.values(EFinancingPlan).filter((x): x is EFinancingPlan => typeof x === 'number'),
)

// Numeric enum mirroring the backend LoanApi.Data.Enums.CoBorrowerRelation (ids match the
// [Loan].[TCoBorrowerRelation] lookup). Replaces the @if/core ERelationToApplicant for loans.
// Labels live locally under translation:enum.ECoBorrowerRelation.<value>.
export enum ECoBorrowerRelation {
  Spouse = 1,
  CommonLaw = 2,
  Employer = 3,
  Employee = 4,
  InLaw = 5,
  Parental = 6,
  Child = 7,
  GrandMother = 8,
  GrandFather = 9,
  GrandChild = 10,
  Brother = 11,
  Sister = 12,
  Other = 13,
}
export const getECoBorrowerRelationList: Getter<ECoBorrowerRelation[]> = lazyLoad(() =>
  Object.values(ECoBorrowerRelation).filter((x): x is ECoBorrowerRelation => typeof x === 'number'),
)
